import { Fragment, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { MoreVertical, PlusCircle } from "react-feather";
import { Col, Row, Button, Container, CardBody, Card, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledDropdown } from 'reactstrap';
import { baseURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";
import axios from "axios";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router";
import useRbac from "../../../../Hooks/useRbac";

const LoyaltyTable = () => {
    const { can } = useRbac();
    const [isLoading, setIsLoading] = useState(false);
    const [loyaltyData, setLoyaltyData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const navigate = useNavigate();


    const handleViewHistory = (userData) => {
        const userId = userData.userId
        navigate(`/loyalty-history/${userId}`);
    };

    const handleViewCustomer = (userId) => {
        const currentIndex = loyaltyData.findIndex(user => user.userId === userId);
        const navigationData = {
            currentIndex: (currentPage - 1) * rowsPerPage + currentIndex,
            totalUsers: totalRows,
            usersList: loyaltyData,
            filters: {
                search: searchTerm,
                page: currentPage,
                limit: rowsPerPage
            }
        };

        navigate(`/customers/customer-detail/${userId}`, {
            state: navigationData
        });
    };

    const loyaltyCols = [
        {
            name: "ID",
            selector: row => row?.userId || "N/A",
            width: "140px",
            cell: (row) => {
                const shortId = row?.userId ? row.userId.slice(-6) : "N/A"; // last 6 chars
                return (
                    <span
                        onClick={() => handleViewHistory(row)}
                        style={{
                            cursor: 'pointer',
                            color: '#6f42c1',
                            fontWeight: '500'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                        {shortId}
                    </span>
                );
            },
            sortable: true,
        },

        {
            name: "Customer Name",
            selector: row => row?.name || "N/A",
            cell: (row) => {
                const displayName = row?.name?.trim() || "N/A";

                return (
                    <div
                        onClick={() => handleViewCustomer(row.userId)}
                        style={{
                            cursor: 'pointer',
                            color: '#007bff',
                            textDecoration: 'none',
                            fontWeight: '500'
                        }}
                        className="customer-name-link"
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                        {displayName}
                    </div>
                );
            },
            sortable: true,
        },
        {
            name: "Customer Email",
            selector: row => row?.email || "N/A",
            width: "250px",
            cell: (row) => (
                row?.email === null || !row?.email ? "N/A" : row?.email
            ),
            sortable: true,
        },
        {
            name: "Phone",
            selector: row => row?.phone || "N/A",
            width: "150px",
            cell: (row) => (
                row?.phone === null || !row?.phone ? "N/A" : row?.phone
            ),
            sortable: true,
        },
        {
            name: "Balance",
            selector: row => row?.balance ?? 0,
            cell: (row) => (
                <span style={{ fontWeight: '600', color: '#28a745' }}>
                    {row?.balance ?? 0} pts
                </span>
            ),
            sortable: true,
        },
        {
            name: "Total Earned",
            selector: row => row?.totalEarned ?? 0,
            cell: (row) => (
                <span style={{ color: '#17a2b8' }}>
                    {row?.totalEarned ?? 0} pts
                </span>
            ),
            sortable: true,
        },
        {
            name: "Total Redeemed",
            selector: row => row?.totalRedeemed ?? 0,
            cell: (row) => (
                <span style={{ color: '#dc3545' }}>
                    {row?.totalRedeemed ?? 0} pts
                </span>
            ),
            sortable: true,
        },
        {
            name: "Total Deducted",
            selector: row => row?.totalDeducted ?? 0,
            cell: (row) => (
                <span style={{ color: '#fd7e14' }}>
                    {row?.totalDeducted ?? 0} pts
                </span>
            ),
            sortable: true,
        },
        {
            name: "History Count",
            selector: row => row?.historyCount ?? 0,
            width: "120px",
            cell: (row) => (
                <span style={{
                    padding: "2px 8px",
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '0.85rem'
                }}>
                    {row?.historyCount ?? 0}
                </span>
            ),
            sortable: true,
            center: true,
        },
        {
            name: "Actions",
            width: "100px",
            cell: (row) => (
                <UncontrolledDropdown className="action_dropdown">
                    <DropdownToggle className="action_btn">
                        <MoreVertical color="#000" size={16} />
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem onClick={() => handleViewHistory(row)}>
                            View History
                        </DropdownItem>
                        {/* <DropdownItem onClick={() => handleExportUserHistory(row.userId || row._id || row.id)}>
                            Export History
                        </DropdownItem> */}
                    </DropdownMenu>
                </UncontrolledDropdown>
            ),
            sortable: false,
            center: true,
        }
    ];

    // Fetch loyalty data for table
    const fetchLoyaltyData = async () => {
        setIsLoading(true);
        try {
            const token = await JSON.parse(localStorage.getItem("token"));
            const response = await axios.get(
                `${baseURL}/api/admin/loyalty/history`,
                {
                    headers: {
                        Authorization: `${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status === 200) {
                let data = response.data.data || [];

                // Apply search filter on frontend since API doesn't support pagination yet
                if (searchTerm) {
                    data = data.filter(user =>
                        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        user.phone?.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                }

                // Apply pagination on frontend
                const startIndex = (currentPage - 1) * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                const paginatedData = data.slice(startIndex, endIndex);

                setLoyaltyData(paginatedData);
                setTotalRows(data.length);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch loyalty data!");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLoyaltyData();
    }, [currentPage, rowsPerPage, searchTerm]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePerPageChange = (newPerPage) => {
        setRowsPerPage(newPerPage);
        setCurrentPage(1);
    };

    const handleExportUserHistory = (userId) => {
        console.log("Exporting history for user:", userId);
        toast.info("Export functionality coming soon!");
    };

    const downloadExcelLoyaltyData = () => {
        console.log("Downloading Excel data for all loyalty records");
        toast.info("Excel export functionality coming soon!");
    };

    const handleNavigateToAddLoyalty = () => {
        navigate('/loyalty-setting');
    }

    return (
        <Fragment>
            <Container fluid={true} style={{ paddingTop: '30px' }}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody style={{ padding: '15px' }}>
                                <Row xxl={12} className='pb-2'>
                                    <Row>
                                        <Col md={6} lg={6} xl={6} xxl={6}>
                                            <div>
                                                <h5 className='mb-0 font-bold'>
                                                    Loyalty Program
                                                </h5>
                                            </div>
                                        </Col>
                                        <Col md={6} lg={6} xl={6} xxl={6}>
                                            <div className="file-content file-content1 justify-content-end">
                                                <div className='mb-0 form-group position-relative search_outer d-flex align-items-center'>
                                                    <i className='fa fa-search' style={{ top: 'unset' }}></i>
                                                    <input
                                                        className='form-control border-0'
                                                        style={{ maxWidth: '195px' }}
                                                        value={searchTerm}
                                                        onChange={handleSearch}
                                                        type='text'
                                                        placeholder='Search loyalty...'
                                                    />
                                                </div>
                                                {can('loyalty.create') && <Button className='btn btn-primary d-flex align-items-center ms-3' onClick={handleNavigateToAddLoyalty}>
                                                    <PlusCircle />
                                                    Add Loyalty Setting
                                                </Button>}
                                            </div>
                                        </Col>
                                    </Row>
                                </Row>
                            </CardBody>

                            <DataTable
                                data={loyaltyData}
                                columns={loyaltyCols}
                                striped={true}
                                center={true}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                paginationPerPage={rowsPerPage}
                                paginationDefaultPage={currentPage}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handlePerPageChange}
                                progressComponent={<Loader />}
                                progressPending={isLoading}
                            />
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default LoyaltyTable;